/**
 * The user menu at the bottom of the sidebar (DEC-198, UI_SDD §R.14), like the Claude
 * app: click your name and a menu opens above it with your email, Settings (yours),
 * Workspace settings (admins, with the number of invitations still waiting), Super Admin
 * (DEC-204, only for Super Admins, with the number of sign-ups waiting for approval, DEC-205),
 * Theme and Log out. Settings left the sidebar's list for it.
 *
 * The theme keeps its switch in the top bar on every screen (DEC-130); the menu item is a
 * second way to the same switch, not a replacement.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronUp, LogOut, Moon, Settings, ShieldCheck, Sun, Wrench } from 'lucide-react'
import { membersApi } from '../../api/members'
import { superAdminApi } from '../../api/superAdmin'
import { useAuth } from '../../hooks/useAuth'
import { currentTheme, subscribe, toggleTheme, type Theme } from '../../theme'
import { PROFILE_LABEL, SETTINGS_PATH } from '../settings/settingsSections'

const itemClass =
  'w-full flex items-center gap-[10px] rounded-sm px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-fill-subtle focus:outline-none focus-visible:bg-fill-subtle'

export function UserMenu({ active }: { active: boolean }) {
  const { claims, signOut } = useAuth()
  const profile = claims?.profile ?? null
  const isAdmin = profile === null || profile === 'admin'
  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useState<Theme>(() => currentTheme())
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => subscribe(setTheme), [])

  // Invitations still waiting, for the badge. The endpoint is admin only, like the page.
  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => membersApi.listInvitations(),
    enabled: isAdmin,
    retry: false,
    staleTime: 60_000,
  })
  const pending = invitations.data?.invitations.filter((i) => i.state === 'pending').length ?? 0

  // DEC-205: website sign-ups waiting for the Super Admin. The endpoint answers 404 to anyone else.
  const signups = useQuery({
    queryKey: ['super-admin', 'signup-requests', 'pending'],
    queryFn: () => superAdminApi.signupRequests('pending'),
    enabled: Boolean(claims?.is_super_admin),
    retry: false,
    staleTime: 60_000,
  })
  const waitingSignups = signups.data?.pending_count ?? 0

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const display = claims?.name ?? claims?.email ?? 'Signed in'

  return (
    <div ref={rootRef} className="relative border-t border-nav-line p-2">
      {open && (
        <div
          role="menu"
          aria-label="Account"
          data-testid="user-menu"
          className="absolute left-2 right-2 bottom-[calc(100%+4px)] z-30 bg-panel text-ink border border-hairline-strong rounded-md shadow-overlay p-1"
        >
          {claims?.email && <div className="px-3 pt-2 pb-1 text-[12px] text-muted truncate">{claims.email}</div>}
          <Link to={SETTINGS_PATH.profile} role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
            <Settings className="w-[15px] h-[15px] text-muted flex-none" />
            <span className="flex-1">Settings</span>
          </Link>
          {isAdmin && (
            <Link to={SETTINGS_PATH.general} role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
              <Wrench className="w-[15px] h-[15px] text-muted flex-none" />
              <span className="flex-1">Workspace settings</span>
              {pending > 0 && (
                <span
                  data-testid="pending-invitations-badge"
                  aria-label={`${pending} ${pending === 1 ? 'invitation' : 'invitations'} waiting`}
                  className="min-w-[18px] rounded-full bg-accent px-[6px] py-[1px] text-center text-[10px] font-semibold tabular-nums text-white"
                >
                  {pending}
                </span>
              )}
            </Link>
          )}
          {/* DEC-204: only for an address the server lists as Super Admin. */}
          {claims?.is_super_admin && (
            <Link
              to={waitingSignups > 0 ? '/super-admin/pending' : '/super-admin/workspaces'}
              role="menuitem"
              className={itemClass}
              onClick={() => setOpen(false)}
            >
              <ShieldCheck className="w-[15px] h-[15px] text-muted flex-none" />
              <span className="flex-1">Super Admin</span>
              {waitingSignups > 0 && (
                <span
                  data-testid="pending-signups-badge"
                  aria-label={`${waitingSignups} ${waitingSignups === 1 ? 'sign-up' : 'sign-ups'} waiting for approval`}
                  className="min-w-[18px] rounded-full bg-accent px-[6px] py-[1px] text-center text-[10px] font-semibold tabular-nums text-white"
                >
                  {waitingSignups}
                </span>
              )}
            </Link>
          )}
          <div className="my-1 border-t border-hairline" />
          <button type="button" role="menuitem" className={itemClass} onClick={() => toggleTheme()}>
            {theme === 'dark' ? (
              <Moon className="w-[15px] h-[15px] text-muted flex-none" />
            ) : (
              <Sun className="w-[15px] h-[15px] text-muted flex-none" />
            )}
            <span className="flex-1">Theme</span>
            <span className="text-[12px] text-muted">{theme === 'dark' ? 'Dark' : 'Light'}</span>
          </button>
          <div className="my-1 border-t border-hairline" />
          <button type="button" role="menuitem" className={itemClass} onClick={() => void signOut()}>
            <LogOut className="w-[15px] h-[15px] text-muted flex-none" />
            <span className="flex-1">Log out</span>
          </button>
        </div>
      )}

      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${display}`}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-[10px] rounded-md px-[6px] py-[6px] text-left transition-colors ${
          open || active ? 'bg-nav-active' : 'hover:bg-nav-hover'
        }`}
      >
        <span className="w-[30px] h-[30px] rounded-full bg-accent text-white font-semibold text-[12px] flex items-center justify-center flex-none">
          {display.charAt(0).toUpperCase()}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold text-[12px] leading-tight truncate text-nav-ink">{display}</span>
          <span className="block text-[10px] leading-tight text-nav-muted truncate">
            {[profile ? PROFILE_LABEL[profile] : 'Admin', claims?.tenant_name].filter(Boolean).join(' · ')}
          </span>
        </span>
        <ChevronUp
          className={`w-[14px] h-[14px] text-nav-muted flex-none transition-transform ${open ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  )
}
